// Web Audio stingers — royalty-free, generated client-side.
// Each stinger is a deterministic sequence of audio events on a shared AudioContext.

export type StingerName = 'logo-reveal' | 'word-reveal' | 'tap' | 'chime';

let ctx: AudioContext | null = null;

function ac(): AudioContext {
  if (!ctx) ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  return ctx;
}

// Resume on first user gesture if browser autoplay-blocked
export function primeAudio() {
  const c = ac();
  if (c.state === 'suspended') c.resume();
}

function envGain(c: AudioContext, t0: number, peak: number, attack: number, decay: number): GainNode {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
  return g;
}

function noiseBuffer(c: AudioContext, durSec: number): AudioBuffer {
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * durSec), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

// FM bell: carrier modulated by a higher-frequency sine for metallic chime
function fmBell(c: AudioContext, t0: number, freq: number, peak: number, decay: number, dest: AudioNode) {
  const carrier = c.createOscillator();
  const mod = c.createOscillator();
  const modGain = c.createGain();

  carrier.type = 'sine';
  carrier.frequency.value = freq;

  mod.type = 'sine';
  mod.frequency.value = freq * 3.5;
  modGain.gain.value = freq * 2.2;

  mod.connect(modGain);
  modGain.connect(carrier.frequency);

  const g = envGain(c, t0, peak, 0.005, decay);
  carrier.connect(g).connect(dest);

  carrier.start(t0);
  mod.start(t0);
  carrier.stop(t0 + decay + 0.05);
  mod.stop(t0 + decay + 0.05);
}

function subThump(c: AudioContext, t0: number, dest: AudioNode) {
  const o = c.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(90, t0);
  o.frequency.exponentialRampToValueAtTime(40, t0 + 0.25);
  const g = envGain(c, t0, 0.55, 0.008, 0.32);
  o.connect(g).connect(dest);
  o.start(t0);
  o.stop(t0 + 0.4);
}

function whoosh(c: AudioContext, t0: number, dest: AudioNode) {
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.6);
  const filt = c.createBiquadFilter();
  filt.type = 'bandpass';
  filt.Q.value = 1.2;
  filt.frequency.setValueAtTime(300, t0);
  filt.frequency.exponentialRampToValueAtTime(6000, t0 + 0.5);
  const g = envGain(c, t0, 0.22, 0.04, 0.5);
  src.connect(filt).connect(g).connect(dest);
  src.start(t0);
}

function shimmer(c: AudioContext, t0: number, dest: AudioNode) {
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.4);
  const filt = c.createBiquadFilter();
  filt.type = 'bandpass';
  filt.Q.value = 3;
  filt.frequency.setValueAtTime(7000, t0);
  filt.frequency.linearRampToValueAtTime(11000, t0 + 0.35);
  const g = envGain(c, t0, 0.18, 0.01, 0.35);
  src.connect(filt).connect(g).connect(dest);
  src.start(t0);
}

function warmPad(c: AudioContext, t0: number, durSec: number, dest: AudioNode) {
  const freqs = [261.63, 329.63, 392.0]; // Cmaj
  const out = c.createGain();
  out.gain.setValueAtTime(0.0001, t0);
  out.gain.exponentialRampToValueAtTime(0.18, t0 + 1.0);
  out.gain.setValueAtTime(0.18, t0 + durSec - 1.0);
  out.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec);

  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 900;
  lp.Q.value = 0.7;
  out.connect(lp).connect(dest);

  for (const f of freqs) {
    [0, 7].forEach((detune) => {
      const o = c.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = detune;
      const g = c.createGain();
      g.gain.value = 0.33;
      o.connect(g).connect(out);
      o.start(t0);
      o.stop(t0 + durSec + 0.05);
    });
  }
}

export function playStinger(name: StingerName, volume = 0.9): { stop: () => void } {
  const c = ac();
  if (c.state === 'suspended') c.resume();
  const master = c.createGain();
  master.gain.value = volume;
  master.connect(c.destination);

  const t = c.currentTime + 0.02;

  if (name === 'logo-reveal') {
    subThump(c, t + 0.20, master);
    whoosh(c, t + 0.80, master);
    fmBell(c, t + 1.00, 523.25, 0.35, 1.4, master);
    fmBell(c, t + 1.22, 659.26, 0.32, 1.3, master);
    fmBell(c, t + 1.44, 783.99, 0.30, 1.2, master);
    fmBell(c, t + 1.66, 1046.5, 0.34, 1.6, master);
    shimmer(c, t + 2.50, master);
    warmPad(c, t + 3.00, 2.0, master);
  } else if (name === 'word-reveal') {
    // Light, classroom-friendly: soft tap + 2-note bell rise
    fmBell(c, t + 0.00, 659.26, 0.28, 0.9, master); // E5
    fmBell(c, t + 0.18, 987.77, 0.30, 1.2, master); // B5
  } else if (name === 'tap') {
    // Soft pluck for sub-element reveals
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, t);
    o.frequency.exponentialRampToValueAtTime(440, t + 0.12);
    const g = envGain(c, t, 0.18, 0.005, 0.15);
    o.connect(g).connect(master);
    o.start(t);
    o.stop(t + 0.2);
  } else if (name === 'chime') {
    fmBell(c, t + 0.0, 1318.51, 0.28, 1.0, master); // E6
  }

  return {
    stop: () => {
      try {
        master.disconnect();
      } catch {}
    },
  };
}
